import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store/store';
import {
  fetchMonthlyOverview,
  planDays,
  planRecurring,
  unplanDays,
} from '../slices/overviewSlice';
import type { DayOverview, MonthlyOverview } from '../slices/overviewSlice';
import { fetchAllUsers } from '../slices/authSlice';
import { useSearchParams } from 'react-router-dom';
import { initialsOf, colorFor } from '../utils/avatar';
import MonthGrid from '../components/MonthGrid';
import DayEditor from '../components/DayEditor';

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 7, label: 'So' },
];
const MAX_MONTHS = 12;

const pad = (n: number) => String(n).padStart(2, '0');
const monthOf = (dateKey: string) => dateKey.slice(0, 7);
const addMonths = (month: string, delta: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};
const firstDayKey = (month: string) => `${month}-01`;
const lastDayKey = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return `${month}-${pad(new Date(y, m, 0).getDate())}`;
};
const monthsBetween = (fromKey: string, toKey: string) => {
  if (fromKey > toKey) return [];
  const end = monthOf(toKey);
  const months: string[] = [];
  let m = monthOf(fromKey);
  while (m <= end && months.length < MAX_MONTHS) {
    months.push(m);
    m = addMonths(m, 1);
  }
  return months;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

const MonthlyTimesheetPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);
  const monthsData = useSelector((state: RootState) => state.overview.monthsData);
  const error = useSelector((state: RootState) => state.overview.error);

  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();

  const [fromDate, setFromDate] = useState(firstDayKey(currentMonthKey()));
  const [toDate, setToDate] = useState(lastDayKey(currentMonthKey()));
  const [selected, setSelected] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [recWeekdays, setRecWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();
  const [recInfo, setRecInfo] = useState<string | undefined>();

  const selfId = user?._id;
  const paramUserId = searchParams.get('userId') || undefined;
  const [viewUserId, setViewUserId] = useState<string | undefined>(
    isAdmin && paramUserId ? paramUserId : selfId
  );
  // Alle Daten-Operationen nutzen die angezeigte Person (Admin kann Mitarbeiter wählen).
  const userId = viewUserId;
  const viewingOther = !!viewUserId && viewUserId !== selfId;
  const viewedName = viewingOther
    ? allUsers.find((u: any) => u._id === viewUserId)?.username || 'Mitarbeiter'
    : null;
  const todayKey = new Date().toLocaleDateString('sv-SE');
  const firstFutureDateKey = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString('sv-SE');
  })();

  const visibleMonths = useMemo(() => monthsBetween(fromDate, toDate), [fromDate, toDate]);
  const rangeInvalid = fromDate > toDate;

  const reloadVisible = () => {
    visibleMonths.forEach((m) => dispatch(fetchMonthlyOverview({ month: m, userId })));
  };

  useEffect(() => {
    visibleMonths.forEach((m) => dispatch(fetchMonthlyOverview({ month: m, userId })));
    setSelected([]);
    setAnchor(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fromDate, toDate, viewUserId]);

  useEffect(() => {
    if (isAdmin) dispatch(fetchAllUsers());
  }, [dispatch, isAdmin]);

  const monthsOverview: MonthlyOverview[] = visibleMonths
    .map((m) => monthsData[m])
    .filter(Boolean) as MonthlyOverview[];

  const allDays: DayOverview[] = useMemo(
    () => monthsOverview.flatMap((mo) => mo.days),
    [monthsOverview]
  );

  const totals = useMemo(() => {
    let soll = 0, ist = 0;
    monthsOverview.forEach((mo) => { soll += mo.totals.soll; ist += mo.totals.ist; });
    soll = Math.round(soll * 100) / 100;
    ist = Math.round(ist * 100) / 100;
    return {
      soll, ist,
      overtime: Math.max(0, Math.round((ist - soll) * 100) / 100),
      undertime: Math.max(0, Math.round((soll - ist) * 100) / 100),
    };
  }, [monthsOverview]);

  const handleDayClick = (day: DayOverview, shiftKey: boolean) => {
    setActionError(undefined);
    if (shiftKey && anchor) {
      const dates = allDays.map((d) => d.date);
      const a = dates.indexOf(anchor);
      const b = dates.indexOf(day.date);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = dates.slice(lo, hi + 1);
        setSelected((prev) => Array.from(new Set([...prev, ...range])));
        return;
      }
    }
    setAnchor(day.date);
    setSelected((prev) =>
      prev.includes(day.date) ? prev.filter((d) => d !== day.date) : [...prev, day.date]
    );
  };

  const clearSelection = () => { setSelected([]); setAnchor(null); };

  const handleDayDoubleClick = (day: DayOverview) => {
    if (!day.planned) return;
    setSelected([day.date]);
    setAnchor(day.date);
    setActionError(undefined);
  };

  const runAction = async (thunk: any, after?: (r: any) => void) => {
    setBusy(true);
    setActionError(undefined);
    try {
      const r = await dispatch(thunk).unwrap();
      reloadVisible();
      clearSelection();
      after?.(r);
    } catch (e: any) {
      setActionError(e?.message || 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const doPlan = () => {
    if (futureUnplannedSelected.length === 0) return;
    runAction(planDays({ dates: futureUnplannedSelected, userId }));
  };
  const doUnplan = () =>
    runAction(unplanDays({ dates: selected.filter((date) => date > todayKey), userId }));

  const toggleRecWeekday = (v: number) =>
    setRecWeekdays((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const doRecurring = () => {
    setRecInfo(undefined);
    const planFrom = fromDate <= todayKey ? firstFutureDateKey : fromDate;
    runAction(
      planRecurring({ from: planFrom, to: toDate, weekdays: recWeekdays, userId }),
      (r) => setRecInfo(`${r.updated} Tage im Zeitraum geplant.`)
    );
  };

  const selectedDay: DayOverview | undefined = useMemo(
    () => (selected.length === 1 ? allDays.find((d) => d.date === selected[0]) : undefined),
    [selected, allDays]
  );

  const futureUnplannedSelected: string[] = useMemo(
    () => selected.filter((date) => {
      const d = allDays.find((x) => x.date === date);
      return d && date > todayKey && !d.planned;
    }),
    [selected, allDays, todayKey]
  );

  const noPlannableSelected = selected.length >= 2 && futureUnplannedSelected.length === 0;

  const quickRange = (months: number) => {
    const base = currentMonthKey();
    setFromDate(firstDayKey(base));
    setToDate(lastDayKey(addMonths(base, months - 1)));
    setRecInfo(undefined);
  };

  // Pfeil-Navigation: verschiebt das Fenster um einen Monat (Spanne bleibt).
  const shiftWindow = (delta: number) => {
    setFromDate(firstDayKey(addMonths(monthOf(fromDate), delta)));
    setToDate(lastDayKey(addMonths(monthOf(toDate), delta)));
    setRecInfo(undefined);
  };

  const rangeLabel =
    visibleMonths.length === 0
      ? monthLabel(monthOf(fromDate))
      : visibleMonths.length === 1
      ? monthLabel(visibleMonths[0])
      : `${monthLabel(visibleMonths[0])} – ${monthLabel(visibleMonths[visibleMonths.length - 1])}`;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 className="mb-0">Zeitplan</h3>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline-secondary" onClick={() => shiftWindow(-1)} title="Vorheriger Monat">‹</button>
          <button className="btn btn-outline-secondary fw-semibold" disabled style={{ minWidth: 210, opacity: 1 }}>{rangeLabel}</button>
          <button className="btn btn-outline-secondary" onClick={() => shiftWindow(1)} title="Nächster Monat">›</button>
        </div>
      </div>

      {/* Admin: moderne Mitarbeiter-Auswahl (Avatare) */}
      {isAdmin && (
        <div className="mb-3">
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>Mitarbeiter</div>
          <div className="d-flex gap-2 flex-nowrap" style={{ overflowX: 'auto', paddingBottom: 4 }}>
            {[
              ...(user ? [{ _id: selfId as string, username: user.username, self: true }] : []),
              ...allUsers.filter((u: any) => u._id !== selfId).map((u: any) => ({ ...u, self: false })),
            ].map((u: any) => {
              const active = u._id === viewUserId;
              return (
                <button
                  key={u._id}
                  type="button"
                  onClick={() => setViewUserId(u._id)}
                  title={u.self ? 'Meine Ansicht' : `Kalender von ${u.username}`}
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    borderRadius: 999,
                    padding: '4px 14px 4px 4px',
                    border: active ? '2px solid #0d6efd' : '1px solid #dee2e6',
                    background: active ? '#e7f1ff' : '#fff',
                    boxShadow: active ? '0 1px 4px rgba(13,110,253,.2)' : 'none',
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto',
                  }}
                >
                  <span
                    style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: u.self ? '#6c757d' : colorFor(u._id),
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 600, flex: '0 0 auto',
                    }}
                  >
                    {u.self ? 'Ich' : initialsOf(u.username)}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: active ? 600 : 400 }}>
                    {u.self ? 'Meine Ansicht' : u.username}
                  </span>
                </button>
              );
            })}
          </div>
          {viewingOther && (
            <div className="mt-2">
              <span className="badge rounded-pill" style={{ background: colorFor(viewUserId || ''), color: '#fff' }}>
                Kalender von {viewedName}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Zeitraum + Planer */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 13 }}>Zeitraum von</label>
              <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 13 }}>bis</label>
              <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="col-auto">
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => quickRange(1)}>1 Monat</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => quickRange(3)}>3 Monate</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => quickRange(6)}>6 Monate</button>
              </div>
            </div>
          </div>

          <hr className="my-3" />

          <label className="form-label mb-1" style={{ fontSize: 13 }}>Anwesenheit planen</label>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            {WEEKDAY_OPTIONS.map((w) => {
              const on = recWeekdays.includes(w.value);
              return (
                <div key={w.value} style={{ width: 64 }}>
                  <button
                    type="button"
                    className={`btn btn-sm w-100 mb-1 ${on ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleRecWeekday(w.value)}
                  >
                    {w.label}
                  </button>
                </div>
              );
            })}
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || rangeInvalid || recWeekdays.length === 0 || toDate < firstFutureDateKey}
              onClick={doRecurring}
            >
              Für Zeitraum planen
            </button>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            Markiert geplante Anwesenheit in zukünftigen Tagen des gewählten Zeitraums.
            Soll- und Iststunden bleiben unverändert und kommen weiter aus Arbeitszeiten und Zeiteinträgen.
          </div>
          {recInfo && <div className="alert alert-success py-2 mt-2 mb-0">{recInfo}</div>}
        </div>
      </div>

      {rangeInvalid && <div className="alert alert-warning py-2">Das Startdatum liegt nach dem Enddatum.</div>}

      {/* Totale */}
      {monthsOverview.length > 0 && (
        <div className="d-flex gap-4 flex-wrap mb-3 p-3 rounded" style={{ background: '#f8f9fa' }}>
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>Soll (bis heute)</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{totals.soll}h</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>Ist</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{totals.ist}h</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>Saldo</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: totals.overtime > 0 ? '#0f5132' : totals.undertime > 0 ? '#842029' : '#212529' }}>
              {totals.overtime > 0 ? '+' : totals.undertime > 0 ? '-' : ''}
              {totals.overtime > 0 ? totals.overtime : totals.undertime}h
            </div>
          </div>
        </div>
      )}

      <p className="text-muted" style={{ fontSize: 13 }}>
        Zukünftige Tage anklicken zum Planen (mehrere möglich, Umschalt+Klick für einen Bereich).
        Der Zeitplan markiert Anwesenheit, verändert aber keine Soll- oder Iststunden.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {actionError && <div className="alert alert-danger py-2">{actionError}</div>}

      <div className="row">
        <div className="col-lg-8 mb-3">
          {monthsOverview.length === 0 ? (
            <p className="text-muted">{rangeInvalid ? '' : 'Lädt…'}</p>
          ) : (
            visibleMonths.map((m) =>
              monthsData[m] ? (
                <div key={m} className="mb-4">
                  <div className="fw-semibold mb-2">{monthLabel(m)}</div>
                  <MonthGrid
                    days={monthsData[m].days}
                    selectedDates={selected}
                    todayKey={todayKey}
                    isAdmin={isAdmin}
                    onDayClick={handleDayClick}
                    onDayDoubleClick={handleDayDoubleClick}
                  />
                </div>
              ) : null
            )
          )}
        </div>
        <div className="col-lg-4">
          <div style={{ position: 'sticky', top: 12 }}>
            {selected.length >= 2 ? (
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{selected.length} Tage ausgewählt</h6>
                    <button className="btn btn-sm btn-link text-decoration-none" onClick={clearSelection}>Auswahl leeren</button>
                  </div>
                  {!noPlannableSelected && futureUnplannedSelected.length < selected.length && (
                    <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                      {selected.length - futureUnplannedSelected.length} bereits geplante oder vergangene Tage werden übersprungen.
                    </div>
                  )}
                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={busy || noPlannableSelected}
                      onClick={doPlan}
                    >
                      Als anwesend geplant markieren
                      {futureUnplannedSelected.length < selected.length && !noPlannableSelected
                        ? ` · ${futureUnplannedSelected.length} Tage`
                        : ''}
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      disabled={busy || selected.every((date) => date <= todayKey)}
                      onClick={doUnplan}
                    >
                      Planung entfernen
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedDay ? (
              <DayEditor
                day={selectedDay}
                month={selectedDay.date.slice(0, 7)}
                userId={userId}
                isAdmin={isAdmin}
                onClose={clearSelection}
              />
            ) : (
              <div className="card">
                <div className="card-body text-muted">
                  Wähle einen oder mehrere zukünftige Tage aus, um Anwesenheit zu planen.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyTimesheetPage;
